export type SoundEvent =
  | 'message_received'
  | 'notification'
  | 'upload_complete'
  | 'download_complete'
  | 'error'
  | 'connection_lost'
  | 'connection_restored'
  | 'call_incoming'
  | 'call_end';

type NotificationPrefsLike = {
  sound?: boolean;
  soundVolume?: number;
};

const SOUND_URLS: Record<SoundEvent, string> = {
  message_received: '/sounds/message_received.wav',
  notification: '/sounds/notification.wav',
  upload_complete: '/sounds/upload_complete.wav',
  download_complete: '/sounds/download_complete.wav',
  error: '/sounds/error.wav',
  connection_lost: '/sounds/connection_lost.wav',
  connection_restored: '/sounds/connection_restored.wav',
  call_incoming: '/sounds/call_incoming.wav',
  call_end: '/sounds/call_end.wav',
};

const DEFAULT_VOLUME = 0.45;
const DEFAULT_COOLDOWN_MS = 1000;
const EVENT_COOLDOWN_MS: Partial<Record<SoundEvent, number>> = {
  message_received: 1500,
  notification: 1200,
  upload_complete: 900,
  download_complete: 900,
  error: 1200,
  connection_lost: 2000,
  connection_restored: 2000,
  call_incoming: 2500,
  call_end: 1200,
};

const audioCache = new Map<SoundEvent, HTMLAudioElement>();
const lastPlayed = new Map<string, number>();

let enabled = true;
let volume = DEFAULT_VOLUME;

function clampVolume(value: unknown): number {
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return DEFAULT_VOLUME;
  if (num > 1) return Math.min(1, Math.max(0, num / 100));
  return Math.min(1, Math.max(0, num));
}

function nowMs(): number {
  return Date.now();
}

function resolveCooldownKey(event: SoundEvent): string {
  if (event === 'message_received') return 'messages';
  return event;
}

function canPlay(event: SoundEvent): boolean {
  const key = resolveCooldownKey(event);
  const cooldown = EVENT_COOLDOWN_MS[event] ?? DEFAULT_COOLDOWN_MS;
  const last = lastPlayed.get(key) ?? 0;
  return nowMs() - last >= cooldown;
}

function rememberPlayed(event: SoundEvent): void {
  lastPlayed.set(resolveCooldownKey(event), nowMs());
}

function getAudio(event: SoundEvent): HTMLAudioElement | null {
  if (typeof Audio === 'undefined') return null;
  const cached = audioCache.get(event);
  if (cached) return cached;
  const audio = new Audio(SOUND_URLS[event]);
  audio.preload = 'auto';
  audioCache.set(event, audio);
  return audio;
}

export function syncNotificationSoundSettings(prefs: NotificationPrefsLike | null | undefined): void {
  enabled = prefs?.sound !== false;
  volume = clampVolume((prefs as any)?.soundVolume);
}

export function playSoundEvent(event: SoundEvent, opts?: { force?: boolean }): boolean {
  if (!enabled && !opts?.force) return false;
  if (!canPlay(event) && !opts?.force) return false;
  const audio = getAudio(event);
  if (!audio) return false;
  audio.volume = volume;
  rememberPlayed(event);
  try { audio.currentTime = 0; } catch {}
  try {
    const res = audio.play();
    if (res && typeof (res as any).catch === 'function') {
      (res as any).catch(() => {});
    }
    return true;
  } catch {
    return false;
  }
}

export function shouldSuppressMessageSound(chatId: string | null | undefined, activeChatId: string | null | undefined): boolean {
  if (!chatId || !activeChatId) return false;
  if (String(chatId) !== String(activeChatId)) return false;
  try {
    if (typeof document === 'undefined') return false;
    const visible = document.visibilityState === 'visible';
    const focused = typeof document.hasFocus === 'function' ? document.hasFocus() : true;
    return visible && focused;
  } catch {
    return false;
  }
}
